import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, OnInit, Renderer2, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { ScrollPanel, ScrollPanelModule } from 'primeng/scrollpanel';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ButtonModule } from 'primeng/button';
import { ToolbarComponent } from '../toolbar/toolbar.component';
import { ProfileComponent } from '../profile/profile.component';
import { MenuComponent } from '../menu/menu.component';
import { BreadcrumbComponent } from '../breadcrumb/breadcrumb.component';
import { LayoutNotificacaoVencimentoComponent } from '../layout-notificacao-vencimento/layout-notificacao-vencimento.component';
import { FooterComponent } from '../footer/footer.component';
import { LoadingComponent } from '../loading/loading.component';

enum MenuOrientation {
  STATIC,
  OVERLAY,
  SLIM,
  HORIZONTAL,
}

@Component({
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.sass'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ScrollPanelModule,
    ToastModule,
    ConfirmDialogModule,
    ButtonModule,
    ToolbarComponent,
    ProfileComponent,
    MenuComponent,
    BreadcrumbComponent,
    LayoutNotificacaoVencimentoComponent,
    FooterComponent,
    LoadingComponent,
  ],
})
export class LayoutComponent implements AfterViewInit, OnDestroy, OnInit {
  renderer2 = inject(Renderer2);
  zone = inject(NgZone);

  private readonly TABLET_BREAKPOINT = 1024;
  private readonly MOBILE_BREAKPOINT = 640;

  layoutCompact = true;

  layoutMode: MenuOrientation = MenuOrientation.STATIC;

  darkMenu = false;

  profileMode = 'inline';

  rotateMenuButton: boolean = false;

  topbarMenuActive: boolean = false;

  overlayMenuActive: boolean = false;

  staticMenuDesktopInactive: boolean = false;

  staticMenuMobileActive: boolean = false;

  rightPanelActive: boolean = false;

  rightPanelClick: boolean = false;

  layoutContainer?: HTMLDivElement;

  layoutMenuScroller?: HTMLDivElement;

  menuClick: boolean = false;

  topbarItemClick: boolean = false;

  activeTopbarItem: unknown;

  resetMenu: boolean = false;

  menuHoverActive: boolean = false;

  @ViewChild('layoutContainer', { static: true })
  layourContainerViewChild?: ElementRef;

  @ViewChild('scrollPanel', { static: true })
  layoutMenuScrollerViewChild?: ScrollPanel;

  rippleInitListener?: EventListener;

  rippleMouseDownListener?: EventListener;

  ngOnInit() {
    this.zone.runOutsideAngular(() => {
      this.bindRipple();
    });
  }

  bindRipple() {
    this.rippleInitListener = this.init.bind(this);
    document.addEventListener('DOMContentLoaded', this.rippleInitListener);
  }

  init() {
    this.rippleMouseDownListener = this.rippleMouseDown.bind(this);
    document.addEventListener('mousedown', this.rippleMouseDownListener, false);
  }

  rippleMouseDown(e: Event) {
    for (
      let target = e.target as HTMLElement | null;
      target && (target as unknown) !== this;
      target = target.parentNode as HTMLElement | null
    ) {
      if (!this.isVisible(target)) {
        continue;
      }
    }
  }

  isVisible(el: HTMLElement) {
    return !!(el.offsetWidth || el.offsetHeight);
  }

  rippleEffect(element: HTMLElement, e: MouseEvent) {
    if (element.querySelector('.ink') === null) {
      const inkEl = document.createElement('span');
      this.addClass(inkEl, 'ink');

      const spanExistente = element.querySelector<HTMLElement>('span');
      if (this.hasClass(element, 'ripplelink') && spanExistente) {
        spanExistente.insertAdjacentHTML('afterend', "<span class='ink'></span>");
      } else {
        element.appendChild(inkEl);
      }
    }

    // Non-null: o bloco acima garante que '.ink' sempre existe neste ponto
    // (já existia, ou foi criado/inserido logo acima).
    const ink = element.querySelector<HTMLElement>('.ink')!;
    this.removeClass(ink, 'ripple-animate');

    if (!ink.offsetHeight && !ink.offsetWidth) {
      const d = Math.max(element.offsetWidth, element.offsetHeight);
      ink.style.height = d + 'px';
      ink.style.width = d + 'px';
    }

    const x = e.pageX - this.getOffset(element).left - ink.offsetWidth / 2;
    const y = e.pageY - this.getOffset(element).top - ink.offsetHeight / 2;

    ink.style.top = y + 'px';
    ink.style.left = x + 'px';
    ink.style.pointerEvents = 'none';
    this.addClass(ink, 'ripple-animate');
  }
  hasClass(element: HTMLElement, className: string) {
    if (element.classList) {
      return element.classList.contains(className);
    } else {
      return new RegExp('(^| )' + className + '( |$)', 'gi').test(
        element.className,
      );
    }
  }

  addClass(element: HTMLElement, className: string) {
    if (element.classList) {
      element.classList.add(className);
    } else {
      element.className += ' ' + className;
    }
  }

  removeClass(element: HTMLElement, className: string) {
    if (element.classList) {
      element.classList.remove(className);
    } else {
      element.className = element.className.replace(
        new RegExp(
          '(^|\\b)' + className.split(' ').join('|') + '(\\b|$)',
          'gi',
        ),
        ' ',
      );
    }
  }

  getOffset(el: HTMLElement) {
    const rect = el.getBoundingClientRect();

    return {
      top:
        rect.top +
        (window.pageYOffset ||
          document.documentElement.scrollTop ||
          document.body.scrollTop ||
          0),
      left:
        rect.left +
        (window.pageXOffset ||
          document.documentElement.scrollLeft ||
          document.body.scrollLeft ||
          0),
    };
  }

  unbindRipple() {
    if (this.rippleInitListener) {
      document.removeEventListener('DOMContentLoaded', this.rippleInitListener);
    }
    if (this.rippleMouseDownListener) {
      document.removeEventListener('mousedown', this.rippleMouseDownListener);
    }
  }

  ngAfterViewInit() {
    this.layoutContainer = <HTMLDivElement>(
      this.layourContainerViewChild?.nativeElement
    );
    setTimeout(() => {
      this.layoutMenuScrollerViewChild?.moveBar();
    }, 100);
  }

  onLayoutClick() {
    this.resetTopbarIfNotClicked();
    this.resetMenuIfNotClicked();
    this.resetRightPanelIfNotClicked();

    this.topbarItemClick = false;
    this.menuClick = false;
    this.rightPanelClick = false;
  }

  private resetTopbarIfNotClicked() {
    if (!this.topbarItemClick) {
      this.activeTopbarItem = null;
      this.topbarMenuActive = false;
    }
  }

  private resetMenuIfNotClicked() {
    if (!this.menuClick) {
      if (this.isHorizontal() || this.isSlim()) {
        this.resetMenu = true;
      }

      if (this.overlayMenuActive || this.staticMenuMobileActive) {
        this.hideOverlayMenu();
      }

      this.menuHoverActive = false;
    }
  }

  private resetRightPanelIfNotClicked() {
    if (!this.rightPanelClick) {
      this.rightPanelActive = false;
    }
  }

  onMenuButtonClick(event: MouseEvent) {
    this.menuClick = true;
    this.rotateMenuButton = !this.rotateMenuButton;
    this.topbarMenuActive = false;

    if (this.layoutMode === MenuOrientation.OVERLAY) {
      this.overlayMenuActive = !this.overlayMenuActive;
    } else {
      if (this.isDesktop()) {
        this.staticMenuDesktopInactive = !this.staticMenuDesktopInactive;
      } else {
        this.staticMenuMobileActive = !this.staticMenuMobileActive;
      }
    }

    event.preventDefault();
  }

  onMenuClick(_event: unknown) {
    this.menuClick = true;
    this.resetMenu = false;
  }

  onTopbarMenuButtonClick(event: MouseEvent) {
    this.topbarItemClick = true;
    this.topbarMenuActive = !this.topbarMenuActive;

    this.hideOverlayMenu();

    event.preventDefault();
  }

  onTopbarItemClick(event: MouseEvent, item: unknown) {
    this.topbarItemClick = true;

    if (this.activeTopbarItem === item) {
      this.activeTopbarItem = null;
    } else {
      this.activeTopbarItem = item;
    }

    event.preventDefault();
  }

  onTopbarSubItemClick(event: MouseEvent) {
    event.preventDefault();
  }

  onRightPanelButtonClick(event: MouseEvent) {
    this.rightPanelClick = true;
    this.rightPanelActive = !this.rightPanelActive;
    event.preventDefault();
  }

  onRightPanelClick() {
    this.rightPanelClick = true;
  }

  hideOverlayMenu() {
    this.rotateMenuButton = false;
    this.overlayMenuActive = false;
    this.staticMenuMobileActive = false;
  }

  isTablet() {
    const width = window.innerWidth;
    return width <= this.TABLET_BREAKPOINT && width > this.MOBILE_BREAKPOINT;
  }

  isDesktop() {
    return window.innerWidth > this.TABLET_BREAKPOINT;
  }

  isMobile() {
    return window.innerWidth <= this.MOBILE_BREAKPOINT;
  }

  isOverlay() {
    return this.layoutMode === MenuOrientation.OVERLAY;
  }

  isHorizontal() {
    return this.layoutMode === MenuOrientation.HORIZONTAL;
  }

  isSlim() {
    return this.layoutMode === MenuOrientation.SLIM;
  }

  changeToStaticMenu() {
    this.layoutMode = MenuOrientation.STATIC;
  }

  changeToOverlayMenu() {
    this.layoutMode = MenuOrientation.OVERLAY;
  }

  changeToHorizontalMenu() {
    this.layoutMode = MenuOrientation.HORIZONTAL;
  }

  changeToSlimMenu() {
    this.layoutMode = MenuOrientation.SLIM;
  }

  ngOnDestroy() {
    this.unbindRipple();
  }
}
